import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, Map, Users, FileText, Shield, Database, 
  BarChart3, TrendingUp, Download, Eye, Share2, 
  FolderOpen, Scale, Cpu, Globe, DownloadCloud,
  ChevronRight, ChevronDown, Menu, X, ExternalLink,
  Info, Mountain, TreePine, MapPin, Home, Phone,
  Award, Zap, BookOpen, Landmark, CheckCircle,
  Filter, Search, Star, Clock, FileDown, Layers,
  ChevronLeft, ChevronUp, Maximize2, Minimize2,
  Play, Pause, RotateCcw, ZoomIn, ZoomOut,
  ArrowDown, Map as MapIcon, FileCode, BookOpen as BookOpenIcon,
  Target, Globe as GlobeIcon, FileCheck, Leaf, Compass,
  Navigation, BarChart, Cloud, Wind, Droplets, Sun,
  Layers as LayersIcon, GitBranch, GitCommit, GitPullRequest,
  AlertCircle, CheckSquare, Clock as ClockIcon, Target as TargetIcon,
  FileSpreadsheet, Book, FileSignature, Users as UsersIcon,
  Building, Trees, Droplet, Filter as FilterIcon,
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  Maximize, Minimize, RefreshCw, Download as DownloadIcon,
  Printer, Share, Copy, BookMarked, Archive,
  FileBarChart, FileImage, FileAudio, FileVideo,
  FileJson, FileXml, FileZip, FileArchive,FileSearch,Paperclip,Loader2,MountainSnow,TreePineIcon
} from 'lucide-react';
import VisorSection from '../../sections/VisorSection';
import BitacoraSection from '../../sections/BitacoraSection';
import FasesSection from '../../sections/FasesSection';
import Gallery from '../../sections/Gallery';
import Descargas from '../../components/Descargas';
import Hero from '../../components/Hero';
import {AboutSection} from '../../sections/AcercaSeccion';

import './SierraOccidental.css';

import DocumentosEspeciales from '../../components/DocumentosEspeciales';
import {DocumentRepository2} from '../../components/DocumentRepository';

// --- DATOS ACTUALIZADOS ---

const DocumentRepository = ({ 
  mainDocs = [], 
  annexDocs = [], 
  title = "Repositorio", 
  subtitle = "Documentación", 
  accentColor = "emerald" 
}) => {
  const [activeTab, setActiveTab] = useState('main');
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  // Mapeo dinámico de colores de Tailwind
  const colorMap = {
    emerald: { text: 'text-emerald-400', border: 'border-emerald-500', bg: 'bg-emerald-500', glow: 'shadow-emerald-500/20' },
    blue: { text: 'text-blue-400', border: 'border-blue-500', bg: 'bg-blue-500', glow: 'shadow-blue-500/20' },
    rose: { text: 'text-rose-400', border: 'border-rose-500', bg: 'bg-rose-500', glow: 'shadow-rose-500/20' }
  };

  const currentTheme = colorMap[accentColor] || colorMap.emerald;

  const filteredDocs = useMemo(() => {
    const list = activeTab === 'main' ? mainDocs : annexDocs;
    return list.filter(doc => 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.subtitle && doc.subtitle.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery, activeTab, mainDocs, annexDocs]);

  const handleDownload = (doc) => {
    if (!doc.url) {
      console.error("No URL provided for this document");
      return;
    }
    
    setDownloadingId(doc.id);
    
    // Simulación de delay de preparación y ejecución de descarga real
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = doc.url;
      link.setAttribute('download', doc.filename || doc.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadingId(null);
    }, 1200);
  };

  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => { setActiveTab(id); setSearchQuery(''); }}
      className={`flex-1 flex items-center justify-center space-x-2 py-4 text-xs font-bold transition-all relative z-10 ${
        activeTab === id ? 'text-white' : 'text-slate-500 hover:text-slate-300'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="uppercase tracking-[0.2em]">{label}</span>
      {activeTab === id && (
        <motion.div
          layoutId="activeTabBg"
          className={`absolute inset-0 bg-gradient-to-r from-slate-800 to-transparent border-b-2 ${currentTheme.border} z-[-1]`}
        />
      )}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto w-full">
      {/* Card de Cabecera */}
      <div className="bg-slate-900/40 rounded-3xl border border-slate-800/50 p-6 md:p-8 mb-6 backdrop-blur-xl shadow-2xl relative overflow-hidden">
        <div className="flex items-center justify-between mb-8 relative z-10">
          <div className="flex items-center space-x-4">
            <div className={`h-14 w-14 rounded-2xl bg-gradient-to-tr from-slate-800 to-slate-700 flex items-center justify-center shadow-lg ${currentTheme.glow}`}>
              <FileText className={`w-7 h-7 ${currentTheme.text}`} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tighter italic">{title}</h1>
              <p className={`text-[10px] md:text-xs ${currentTheme.text} font-bold uppercase tracking-[0.4em]`}>{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Buscador */}
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-white transition-colors" />
          <input 
            type="text"
            placeholder="Buscar en este repositorio..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-black/40 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-base focus:outline-none focus:ring-1 focus:ring-slate-700 transition-all placeholder:text-slate-600 shadow-inner text-white"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-900/20 rounded-t-2xl border-x border-t border-slate-800/50 overflow-hidden backdrop-blur-md">
        <TabButton id="main" label="Documentos" icon={Layers} />
        <TabButton id="annex" label="Anexos" icon={Paperclip} />
      </div>

      {/* Lista */}
      <div className="bg-slate-900/20 border-x border-b border-slate-800/50 rounded-b-3xl p-3 min-h-[400px] backdrop-blur-md shadow-xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + searchQuery}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            {filteredDocs.length > 0 ? (
              filteredDocs.map((doc) => (
                <div key={doc.id} className="group relative flex items-center bg-slate-900/40 hover:bg-slate-800/70 border border-slate-800/40 rounded-2xl p-4 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-black/60 flex items-center justify-center mr-4">
                    {doc.icon || <FileText className="w-5 h-5 text-slate-500" />}
                  </div>
                  <div className="flex-grow min-w-0 pr-4">
                    <h3 className="text-base font-bold text-slate-100 truncate group-hover:text-white">{doc.title}</h3>
                    <p className="text-xs text-slate-500 truncate italic">{doc.subtitle || "Archivo disponible para descarga."}</p>
                  </div>
                  <button 
                    onClick={() => handleDownload(doc)}
                    disabled={downloadingId === doc.id}
                    className={`flex-shrink-0 flex items-center space-x-2 px-4 py-2.5 rounded-xl font-bold text-xs transition-all ${
                      downloadingId === doc.id ? 'bg-white/10 text-white' : `bg-white/5 text-slate-400 hover:text-white hover:${currentTheme.bg}`
                    }`}
                  >
                    {downloadingId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </button>
                </div>
              ))
            ) : (
              <div className="py-24 text-center">
                <FileSearch className="w-10 h-10 text-slate-800 mx-auto mb-4" />
                <p className="text-slate-500 text-sm">No se encontraron resultados</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
const sierraMain = [
  {id: 0, title: "Publicación en  Periódico Oficial", subtitle: "Versión completa", icon: <Book className="w-5 h-5 text-green-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/PUBLICACION%20DEL%20PERI%C3%93DICO%20OFICIAL%20PROGRAMA%20DE%20ORDENAMIENTO%20ECOLOGICO%20Y%20TERRITORIAL%20REGIONAL%20SIERRA%20OCCIDENTAL.pdf"
   },
    { id: 1, title: "POETR Sierra Occidental", subtitle: "Versión abreviada", icon: <Map className="w-5 h-5 text-emerald-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Regi%C3%B3n%20Sierra%20Occidental.pdf"
     },
    { id: 2, title: "A. Guía de lectura", subtitle: "Introducción", icon: <BookOpen className="w-5 h-5 text-blue-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/A.%20Gu%C3%ADa%20de%20lectura%20-%20Introducci%C3%B3n.pdf"
     },
    { id: 3, title: "B. Bases técnicas", subtitle: "Metodología", icon: <FileSearch className="w-5 h-5 text-purple-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/B.%20Bases%20t%C3%A9cnicas.pdf"
     },
    { id: 4, title: "C. Estructura POETR", subtitle: "Contenido detallado", icon: <Layers className="w-5 h-5 text-amber-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/C.%20Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Territorial%20Regional.docx.pdf"
     },
    { id: 5, title: "D. Plan Integración Urbana", subtitle: "Desarrollo Regional", icon: <Target className="w-5 h-5 text-rose-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Plan%20Regional%20de%20Integraci%C3%B3n%20Urbana%20Sierra%20Occidental.pdf"
     },
{ id: '6.1', title: "Proyectos estratégicos", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%203.%20Proyectos%20estrat%C3%A9gicos.pdf" },
    { id: '6.2', title: "Indicadores", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%207.%20Indicadores.pdf" },
    { id: '6.3', title: "Apéndice técnico", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%208.%20Ap%C3%A9ndice%20t%C3%A9cnico.pdf" },
    { id: '6.4', title: "Agenda territorial", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%209.%20Agenda%20Territorial.pdf" }
  ];

  const sierraAnnex = [
    { id: '6.1', title: "Proyectos estratégicos", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%203.%20Proyectos%20estrat%C3%A9gicos.pdf" },
    { id: '6.2', title: "Indicadores", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%207.%20Indicadores.pdf" },
    { id: '6.3', title: "Apéndice técnico", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%208.%20Ap%C3%A9ndice%20t%C3%A9cnico.pdf" },
    { id: '6.4', title: "Agenda territorial", category: "Anexos", url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Anexo%209.%20Agenda%20Territorial.pdf" }
  ];

const TIMELINE_DATA3 = [
  { 
    id: 1,
    date: 'Nov 2022', 
    event: 'Inicio del Proceso', 
    status: 'completed',
    description: 'Firma del convenio y conformación del equipo técnico',
    icon: FileCheck,
    duration: '3 meses',
    participants: 15,
    documents: ['POET-IN-001', 'POET-IN-002']
  },
  { 
    id: 2,
    date: 'Feb 2023', 
    event: 'Diagnóstico Integral', 
    status: 'completed',
    description: 'Estudios técnicos ambientales y consultas públicas regionales',
    icon: Search,
    duration: '9 meses',
    participants: 45,
    documents: ['POET-DI-001', 'POET-DI-002', 'POET-DI-003'],
    milestones: ['Estudio hidrológico', 'Análisis de suelo', 'Consulta ciudadana']
  },
  { 
    id: 3,
    date: 'Oct 2023', 
    event: 'Propuesta Técnica', 
    description: 'Elaboración del documento base y validación técnica',
    icon: BookOpen,
    duration: '8 meses',
    participants: 28,
    documents: ['POET-PT-001', 'POET-PT-002'],
    milestones: ['Modelo predictivo', 'Propuesta de zonificación', 'Revisión por pares']
  },
  { 
    id: 4,
    date: 'Nov 2024', 
    event: 'Publicación Oficial', 
    status: 'completed',
    description: 'Revisión legal y publicación en Periódico Oficial',
    icon: Award,
    duration: '1 mes',
    participants: 32,
    documents: ['POET-AP-001', 'POET-AP-002', 'POET-AP-003'],
    milestones: ['Dictamen legal', 'Publicación oficial', 'Presentación pública'],
    highlight: true
  },
  { 
    id: 5,
    date: 'Actual', 
    event: 'Implementación Activa', 
    status: 'current',
    description: 'Ejecución de proyectos piloto y seguimiento continuo',
    icon: Zap,
    duration: 'En curso',
    participants: 62,
    documents: ['POET-IM-001', 'POET-IM-002', 'POET-IM-003', 'POET-IM-004'],
    milestones: ['Proyecto piloto A', 'Sistema de monitoreo', 'Capacitaciones'],
    progress: 65,
    active: true
  },
  { 
    id: 6,
    date: '2026', 
    event: 'Primera Evaluación', 
    status: 'pending',
    description: 'Revisión de resultados, impactos y ajustes del programa',
    icon: BarChart3,
    duration: '6 meses (plan)',
    participants: 24,
    documents: [],
    milestones: ['Evaluación de impacto', 'Informe de resultados', 'Ajustes al programa']
  }
];

// Documentos Especiales (movidos arriba)
const SPECIAL_DOCUMENTS = [
  {
    id: 1,
    code: 'POET-ES-001',
    title: 'Atlas de Biodiversidad Regional',
    description: 'Inventario completo de especies y ecosistemas de la Sierra Occidental',
    category: 'especial',
    tags: ['biodiversidad', 'atlas', 'completo'],
    format: 'PDF',
    size: '42.8 MB',
    pages: 245,
    date: '15 Mar 2024',
    lastModified: '15 Mar 2024',
    starred: true,
    downloads: 892,
    views: 2145,
    version: '1.0',
    author: 'Departamento de Biología',
    status: 'publicado',
    icon: Trees,
    downloadUrl: '#',
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 2,
    code: 'POET-ES-002',
    title: 'Análisis Hidrológico Integral',
    description: 'Estudio detallado de cuencas, acuíferos y recursos hídricos',
    category: 'especial',
    tags: ['hidrología', 'acuíferos', 'cuencas'],
    format: 'PDF',
    size: '35.2 MB',
    pages: 189,
    date: '10 Abr 2024',
    lastModified: '12 Abr 2024',
    starred: true,
    downloads: 745,
    views: 1789,
    version: '2.1',
    author: 'Equipo Hidrológico',
    status: 'aprobado',
    icon: Droplet,
    downloadUrl: '#',
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 3,
    code: 'POET-ES-003',
    title: 'Mapa de Riesgos Ambientales',
    description: 'Cartografía de riesgos naturales y antrópicos del territorio',
    category: 'especial',
    tags: ['riesgos', 'cartografía', 'ambiental'],
    format: 'ZIP',
    size: '58.3 MB',
    pages: 156,
    date: '05 May 2024',
    lastModified: '10 May 2024',
    starred: false,
    downloads: 623,
    views: 1542,
    version: '1.2',
    author: 'Departamento de Cartografía',
    status: 'vigente',
    icon: MapIcon,
    downloadUrl: '#',
    color: 'from-amber-500 to-orange-600'
  },
  {
    id: 4,
    code: 'POET-ES-004',
    title: 'Informe Socioeconómico Detallado',
    description: 'Análisis de indicadores sociales y económicos de la población',
    category: 'especial',
    tags: ['socioeconómico', 'indicadores', 'población'],
    format: 'PDF',
    size: '28.7 MB',
    pages: 134,
    date: '20 Jun 2024',
    lastModified: '25 Jun 2024',
    starred: true,
    downloads: 534,
    views: 1321,
    version: '3.0',
    author: 'Equipo de Ciencias Sociales',
    status: 'publicado',
    icon: UsersIcon,
    downloadUrl: '#',
    color: 'from-purple-500 to-violet-600'
  }
];

// Documentos Técnicos (movidos abajo)
const TECHNICAL_DOCUMENTS = [
  {
    id: 1,
    code: 'POET-001',
    title: 'Decreto de Aprobación Oficial',
    description: 'Documento oficial de aprobación y publicación en Periódico Oficial del Estado',
    category: 'legal',
    tags: ['oficial', 'legal', 'publicado'],
    format: 'PDF',
    size: '2.4 MB',
    pages: 42,
    date: '15 Oct 2024',
    lastModified: '15 Oct 2024',
    starred: true,
    downloads: 1245,
    views: 3287,
    version: '1.0',
    author: 'Departamento Legal',
    status: 'publicado',
    icon: Shield,
    downloadUrl: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Regi%C3%B3n%20Sierra%20Occidental.pdf',
    viewUrl: '#',
    metadata: {
      resolution: 'ACUERDO-2024-001',
      entity: 'Secretaría de Medio Ambiente',
      validity: 'Indefinida'
    }
  },
  {
    id: 2,
    code: 'POET-012',
    title: 'Plan de Integración Urbana',
    description: 'Estrategias de desarrollo urbano sustentable para la región',
    category: 'planning',
    tags: ['planificación', 'urbano', 'sustentable'],
    format: 'PDF',
    size: '8.7 MB',
    pages: 156,
    date: '10 Sep 2024',
    lastModified: '05 Oct 2024',
    starred: true,
    downloads: 856,
    views: 2145,
    version: '2.1',
    author: 'Coordinación de Planeación',
    status: 'aprobado',
    icon: MapIcon,
    downloadUrl: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Plan%20Regional%20de%20Integraci%C3%B3n%20Urbana%20Sierra%20Occidental.pdf',
    viewUrl: '#',
    metadata: {
      samples: 245,
      methodology: 'ANÁLISIS-GIS-2024',
      period: '2021-2023'
    }
  }
];

// Datos para las tarjetas de descarga
const Items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png",
    baseColor: "blue",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/VectorialesSierraOccidental.zip",
    target: "_blank",
    icon: LayersIcon
  },
  {
    title: "Fichas UGAs",
    description: "Información detallada de cada Unidad de Gestión Ambiental.",
    imageUrl: "images/FICHAS_UGA.png",
    baseColor: "emerald",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Fichas%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Sierra%20Occidental.pdf",
    target: "_blank",
    icon: FileText
  },
  {
    title: "Criterios Ecológicos",
    description: "Regulaciones para preservación y restauración del territorio.",
    imageUrl: "images/Criterios.png",
    baseColor: "amber",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Criterios%20de%20regulaci%C3%B3n%20ecol%C3%B3gica%20Regi%C3%B3n%20Sierra%20Occidental.pdf",
    target: "_blank",
    icon: TargetIcon
  },
  {
    title: "Estrategias",
    description: "Proyectos estratégicos para protección del entorno natural.",
    imageUrl: "images/Estrategias.png",
    baseColor: "rose",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Proyectos%20estrat%C3%A9gicos%20Regi%C3%B3n%20Sierra%20Occidental.pdf",
    target: "_blank",
    icon: GitBranch
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales.",
    imageUrl: "images/Lineamientos.png",
    baseColor: "indigo",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Sierra%20Occidental.pdf",
    target: "_blank",
    icon: GitCommit
  },
  {
    title: "Compatibilidades",
    description: "Guia de compatibilidad de usos del suelo estratégicos.",
    imageUrl: "images/Compatibilidades.png",
    baseColor: "teal",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Descargas/Listado%20de%20usos%20y%20actividades%20Regi%C3%B3n%20Sierra%20Occidental.pdf",
    target: "_blank",
    icon: GlobeIcon
  },
];

const municipalities = [
  {
    id: 1,
    name: 'Atenguillo',
    shield: 'images/Escudo_Atenguillo.png',
    description: 'Con agricultura de temporal, ganadería menor y producción forestal local, se ubica en valles y sierras con bosques de pino–encino y ríos intermitentes. Su entorno natural permite el ecoturismo rural, la observación de flora y fauna.',
    population: '4,176 habitantes',
    area: '610.2 km²',
    foundation: '1885',
    attractions: 'El río Atenguillo y arroyos como El Salitre y Mixtlán, junto con bosques de pino, encino y roble en sierras como Comalito y Peña Tendida, conforman paisajes ideales para senderismo y recorridos rurales. Destacan además las Cascadas El Salitre y El Limón, así como los petrograbados de “Las Pintadas” en el rancho Las Guajadas.',
    color: 'from-blue-500 to-cyan-500',
    NumerodeUgas: '12',
    EcologicaZona: 'Área Natural Protegida Área de Protección de Recursos Naturales CADNR 043 Estado de Nayarit.',
    administrativeRegion: 'Sierra Occidental',
  },
  {
    id: 2,
    name: 'Guachinango',
    shield: 'images/Escudo_Guachinango.png',
    description: 'Destacado por la agricultura de temporal, ganadería y extracción de madera, el municipio se desarrolla en sierras y cañadas con bosques de pino–encino y ríos de montaña. Su territorio favorece el ecoturismo, la conservación de ecosistemas forestales.',
    population: '4,199 habitantes',
    area: '837.7 km²',
    foundation: '1885',
    attractions: 'Ríos Ameca y Atenguillo con numerosos arroyos, los Ojos de Agua como El Aguacate y Las Higueras, así como la Presa Jesús Rodríguez. El municipio sobresale por sus paisajes serranos y bosques de pino y encino que permiten senderismo, observación natural y recorridos entre montañas y áreas rurales.',
    color: 'from-emerald-500 to-green-500',
    NumerodeUgas: '13',   
    EcologicaZona: 'Área Natural Protegida Área de Protección de Recursos Naturales CADNR 043 Estado de Nayarit y Reserva de la Biosfera Sierra de Vallejo-Río Ameca.',
    administrativeRegion: 'Sierra Occidental',
  },
  {
    id: 3,
    name: 'Mascota',
    shield: 'images/Escudo_Mascota.png',
    description: 'Con agricultura, ganadería y producción forestal, se localiza en valles y sierras con ríos permanentes y bosques de pino–encino que permiten la implementación de proyectos de conservación ambiental. Su entorno favorece el turismo rural, senderismo.',
    population: '14,451 habitantes',
    area: '1,843.1 km²',
    foundation: '1910',
    attractions: 'Laguna de Juanacatlán, Presa Corrinchis, manantial El Colomo y los ríos Mascota y Talpa, ideales para paisajes y actividades al aire libre. El municipio destaca por extensos bosques de pino y encino, senderismo en zonas como Mesa Colorada y El Coamil, además de patrimonio histórico como la Parroquia y la Hacienda El Atajo.',
    color: 'from-amber-500 to-orange-500',
    NumerodeUgas: '22',
    EcologicaZona: 'Área Natural Protegida Área de Protección de Recursos Naturales CADNR 043 Estado de Nayarit, Reserva de la Biosfera Sierra de Vallejo-Río Ameca y Área Estatal de Protección Hidrológica Sierra El Cuale.',
    administrativeRegion: 'Sierra Occidental',
  },
  {
    id: 4,
    name: 'Mixtlán',
    shield: 'images/Escudo_Mixtlan.png',
    description: 'Dedicado a la agricultura de temporal, ganadería menor y aprovechamiento forestal, el municipio se encuentra en lomeríos y cañadas con bosques de pino–encino y ríos intermitentes. Sus paisajes naturales facilitan el ecoturismo rural.',
    population: '3,638 habitantes',
    area: '631 km²',
    foundation: '1938',
    attractions: 'El río Atenguillo y arroyos como El Salitre y Mixtlán, junto con bosques de pino, encino y roble en sierras como Comalite, Mesa Colorado y Peña Tendida, ideales para senderismo y paisajes. Además, el rancho Las Guajadas destaca por petrograbados conocidos como “Las Pintadas”.',

    color: 'from-violet-500 to-purple-500',
    NumerodeUgas: '9',
    EcologicaZona: 'Área Natural Protegida Área de Protección de Recursos Naturales CADNR 043 Estado de Nayarit y Reserva de la Biosfera Sierra de Vallejo-Río Ameca.',
    administrativeRegion: 'Sierra Occidental',
  },
  {
    id: 5,
    name: 'San Sebastián del Oeste',
    shield: 'images/Escudo_San_Sebastian_del_Oeste.png',
    description: 'Con agricultura de temporal, ganadería menor y actividad turística cultural, se desarrolla en sierras con bosques de pino–encino y ríos de montaña. Su territorio favorece el ecoturismo de montaña y la conservación de ecosistemas.',
    population: '5,086 habitantes',
    area: '1,116.7 km²',
    foundation: '1824',
    attractions: 'Ríos como el Ameca y Los Reyes, la presa La Paloma y paisajes serranos ideales para caminatas. Destacan los bosques de pino y encino alrededor del Cerro de La Cumbre, con amplias vistas naturales, además de antiguas haciendas y la imagen histórica del pueblo..',
    color: 'from-fuchsia-500 to-pink-500',
    NumerodeUgas: '12',
    EcologicaZona: 'Área Natural Protegida Área de Protección de Recursos Naturales CADNR 043 Estado de Nayarit, Reserva de la Biosfera Sierra de Vallejo-Río Ameca y Área Estatal de Protección Hidrológica Sierra El Cuale.',
    administrativeRegion: 'Sierra Occidental',
  },
  {
    id: 6,
    name: 'Talpa de Allende',
    shield: 'images/Escudo_Talpa_de_Allende.png',
    description: 'Destacado por la agricultura de temporal, ganadería y turismo religioso y de naturaleza, se ubica en lomeríos y sierras con ríos y bosques de pino–encino. Su entorno natural permite la observación de fauna y flora y la implementación de prácticas de conservación.',
    population: '14,997 habitantes',
    area: '1,996.4 km²',
    foundation: '1824',
    attractions: 'Ríos como Talpa y Cuale, y paisajes boscosos en la Sierra de El Cuale y la Cumbre de Guadalupe, ideales para senderismo. Destacan la cascada de Aranjuez y miradores naturales, junto con su centro religioso encabezado por el Santuario de la Virgen de Talpa..',
    color: 'from-rose-500 to-red-500',
    NumerodeUgas: '18',
    EcologicaZona: 'Área Natural Protegida Área Estatal de Protección Hidrológica Sierra El Cuale y Parque Estatal Bosque de Arce.',
    administrativeRegion: 'Sierra Occidental',
  }
];

// Bitácora Ambiental COMPLETA y MODERNIZADA
const bitacoraItems = [
  {
    title: 'FASE DE FORMULACIÓN',
    icon: FileSignature,
    color: 'from-blue-500 to-cyan-500',
    content: [
      {
        subtitle: 'Acuerdos de Integración Municipal',
        items: [
          { text: 'Acuerdo de integración - Atenguillo', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Atenguillo.pdf' },
          { text: 'Acuerdo de integración - Guachinango', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Guachinango.pdf' },
          { text: 'Acuerdo de integración - Mascota', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Mascota.pdf' },
          { text: 'Acuerdo de integración - Mixtlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Mixtl%C3%A1n.pdf' },
          { text: 'Acuerdo de integración - San Sebastian del Oeste', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20San%20Sebastian%20del%20Oeste.PDF' }
        ]
      },
      {
        subtitle: 'Actas de Aprobación',
        items: [
          { text: 'Acta de aprobación - Atenguillo POETR Y PMDU', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Atenguillo%20POETR%20Y%20PMDU.pdf' },
          { text: 'Acta de aprobación - Guachinango POETR Y PMDU', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Guachinango%20POETR%20Y%20PMDU.pdf' },
          { text: 'Acta de aprobación - Mascota POETR Y PMDU', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Mascota%20%20POETR%20Y%20PMDU.pdf' },
          { text: 'Acta de aprobación - Mixtlan PMDU', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Mixtlan%20PMDU.PDF' },
          { text: 'Acta de aprobación - Mixtlan POETR', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Mixtlan%20POETR.PDF' },
          { text: 'Acta de aprobación - San Sebastian del Oeste POETR Y PMDU', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20San%20Sebastian%20del%20Oeste%20POETR%20Y%20PMDU.pdf' }
        ]
      },
      {
        subtitle: 'Convenios',
        items: [
          { text: 'Convenio Marco de Coordinación Estado - Federación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/verde/Convenio/ConvenioMarcoEstado_Federacion.pdf' },
          { text: 'Convenio de Coordinación Estado-Municipios "Región Sierra Occidental"', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Convenio/publicacion_OET_Sierra_Occidental.pdf' }
        ]
      }
    ]
  },
  {
    title: 'FASE DE EXPEDICIÓN',
    icon: FileSpreadsheet,
    color: 'from-emerald-500 to-green-500',
    content: [
      {
        subtitle: 'Participación Ciudadana',
        items: [
          { text: 'Listas de Asistencia Foro taller de Participación Ciudadana', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Elaboracion/01_Listas_de_Asistencia_Foro_taller_de_Participacion_ciudadana.pdf' },
          { text: 'Respuestas a las Observaciones de la Consulta Pública', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Elaboracion/Respuestas_a_las_observaciones_de_la_consulta_publica_RSO.pdf' }
        ]
      },
      {
        subtitle: 'Documentos Técnicos',
        items: [
          { text: 'Programa de Ordenamiento Ecológico Territorial Regional de la Sierra Occidental', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Elaboracion/documentos/' },
          { text: 'Publicación Periódico Oficial del Estado Región Sierra Occidental', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Elaboracion/POERT_Sierra_Occidental_Peri%C3%B3dico.pdf' }
        ]
      }
    ]
  },
  {
    title: 'FASE DE EJECUCIÓN',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    content: [
      {
        subtitle: 'Actas del Consejo Regional',
        items: [
          { text: 'Acta de Instalación y Primera Ordinaria', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Consejo/Acta_de_instalacion_1er_ordinaria_CR_SOCC.pdf' },
          { text: 'Lista de asistencia instalación y 1er ordinaria', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Consejo/Lista_Asistencia_instalacion_y_1er_ord_%20CR%20SOCC.pdf' },
          { text: 'Acta Primera Sesión 2023 CR Sierra Occidental', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Consejo/Acta_Primera_Sesion_2023_CR_Sierra_Occidental_Censurado.pdf' },
          { text: 'Acta Segunda Sesión 2023 CR Sierra Occidental', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Consejo/Acta_de_la_Segunda_Sesion_CRSO_Validacion_Censurado.pdf' },
          { text: 'Lista de asistencia Segunda Sesión 2023', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Consejo/Lista_de_Asistencia_CRSO_segunda_sesion_2023_Censurado.pdf' }
        ]
      },
      {
        subtitle: 'Seguimiento y Monitoreo',
        items: [
          { text: 'Boletas Registrales Sierra Occidental', url: '#https://siga.jalisco.gob.mx/ordenamiento/archivos/Occidental/Ejecucion/BOLETAS_REGISTRALES_DE_SIERRA_OCCIDENTAL.pdf' },
          
        ]
      }
    ]
  },
  {
    title: 'FASE DE EVALUACIÓN',
    icon: BarChart3,
    color: 'from-purple-500 to-violet-500',
    content: [
      {
        subtitle: 'Evaluaciones Programadas',
        items: [
          // { text: 'Plan de Evaluación Anual 2025', url: '#' },
          // { text: 'Metodología de Evaluación de Impacto', url: '#' },
          // { text: 'Indicadores de Desempeño Ambiental', url: '#' }
        ]
      },
      {
        subtitle: 'Ajustes y Mejoras',
        items: [
          // { text: 'Protocolo de Ajustes al Programa', url: '#' },
          // { text: 'Propuestas de Mejora Continua', url: '#' },
          // { text: 'Retroalimentación de Usuarios', url: '#' }
        ]
      }
    ]
  }
];

// --- COMPONENTES ACTUALIZADOS ---

// Componente DetailBadge
const DetailBadge = ({ icon: Icon, label, value }) => (
  <motion.div 
    whileHover={{ scale: 1.02 }}
    className="flex items-start p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm 
      hover:shadow-md transition-all border border-gray-100 dark:border-gray-700"
  >
    <div className="p-2 bg-gradient-to-r from-blue-100 to-teal-100 dark:from-blue-900/30 dark:to-teal-900/30 
      rounded-lg mr-4">
      <Icon className="text-blue-600 dark:text-blue-400" size={24} />
    </div>
    <div>
      <span className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <span className="block text-lg font-bold text-gray-900 dark:text-white leading-tight">{value}</span>
    </div>
  </motion.div>
);

// Status Badge Moderno
const StatusBadge = () => {
  return (
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 
        backdrop-blur-md border border-emerald-500/30"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2" />
      <span className="text-sm font-medium text-white tracking-wider">FASE </span>
      <div className="ml-2 pl-2 border-l border-white/20">
        <span className="text-xs text-white/70">En Ejecución</span>
      </div>
    </motion.div>
  );
};

// Card de Documento Especial Moderna
const SpecialDocumentCard = ({ doc, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = doc.icon || FileText;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative overflow-hidden rounded-2xl"
    >
      {/* Fondo gradiente animado */}
      <div className={`absolute inset-0 bg-gradient-to-br ${doc.color} opacity-0 group-hover:opacity-10 
        transition-opacity duration-500 rounded-2xl`} />
      
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl 
        transition-all duration-300 border border-gray-100 dark:border-gray-700">
        
        {/* Header con icono */}
        <div className="flex justify-between items-start mb-6">
          <div className={`p-4 rounded-xl bg-gradient-to-br ${doc.color} shadow-md`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div className="flex items-center gap-2">
            {doc.starred && (
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            )}
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase 
              bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full">
              {doc.size}
            </span>
          </div>
        </div>

        {/* Contenido */}
        <div className="mb-6">
          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full 
            bg-gradient-to-r from-blue-500/10 to-teal-500/10 text-blue-600 dark:text-blue-400 mb-3">
            {doc.code}
          </span>
          
          <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
            {doc.title}
          </h4>
          
          <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
            {doc.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {doc.tags?.slice(0, 3).map((tag, i) => (
              <span 
                key={i}
                className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 
                  text-gray-700 dark:text-gray-300 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {doc.date}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {doc.views?.toLocaleString()}
              </span>
            </div>
            <span className="font-medium">{doc.version}</span>
          </div>
        </div>

        {/* Botón de acción */}
        <motion.a
          href={doc.downloadUrl || '#'}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`block w-full py-3.5 rounded-xl text-center font-semibold 
            bg-gradient-to-r ${doc.color} text-white shadow-lg hover:shadow-xl 
            transition-all flex items-center justify-center gap-3`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Download className="w-5 h-5" />
          Descargar Documento
          <ExternalLink className="w-4 h-4" />
        </motion.a>
      </div>
    </motion.div>
  );
};

// Card de Documento Técnico (más simple)
const TechnicalDocumentCard = ({ doc, index }) => {
  const Icon = doc.icon || FileText;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md hover:shadow-lg 
        transition-all duration-300 border border-gray-100 dark:border-gray-700"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-600">
          <Icon size={20} />
        </div>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 
          bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {doc.size}
        </span>
      </div>

      <div className="mb-4">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
          {doc.title}
        </h4>
        
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {doc.description}
        </p>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {doc.date}
        </span>
        
        <motion.a
          href={doc.downloadUrl || '#'}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="flex items-center justify-center w-8 h-8 rounded-full 
            bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow hover:shadow-md 
            transition-all"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Download className="w-4 h-4" />
        </motion.a>
      </div>
    </motion.div>
  );
};

// Banner Moderno con fondo menos oscuro
const ModernBanner = ({ onScrollClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stats = [
    { value: '6', label: 'Municipios', icon: MapPin },
    { value: '15,842', label: 'km²', icon: Map },
    { value: '2024', label: 'Publicación', icon: Calendar },
    { value: 'Vigente', label: 'Estado', icon: CheckCircle },
  ];

  return (
    <section className="relative h-screen min-h-[780px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="images/SierraOccidental.jpeg"
          alt="Paisaje Sierra Occidental"
          className="w-full h-full object-cover"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
      </div>

      <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6">
                <StatusBadge />
              </div>

              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  Región Sierra Occidental
                </span>
              </h1>

              <p className="text-xl text-gray-200 mb-8 max-w-xl">
                Estrategia integral para la gestión sustentable del territorio, equilibrando el desarrollo humano con la preservación de los ecosistemas de la región.
              </p>

              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => onScrollClick('introduccion')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all"
                >
                  Explorar Región
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <ArrowDown className="text-cyan-400" size={32} />
      </motion.div>
    </section>
  );
};

// Visor Completo con Sidebar
const FullWidthVisorWithSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('uga');

  return (
    <section id="visor-completo" className="py-16">
      <div className="max-w-[2000px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Plataforma Geoespacial Completa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistema de información geográfica integrado con documentación técnica completa
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Ancho fijo */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`lg:w-96 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 sticky top-6">
              {/* Botón toggle para móviles */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mb-4 w-full p-2 bg-gradient-to-r from-blue-500 to-teal-500 
                  text-white rounded-lg flex items-center justify-center gap-2"
              >
                {sidebarOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
                <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : ''}`} />
              </button>

              {/* Tabs de navegación */}
              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('uga')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'uga'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Clasificación UGA
                </button>
                <button
                  onClick={() => setActiveTab('descripcion')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'descripcion'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Descripción UGA
                </button>
                <button
                  onClick={() => setActiveTab('politica')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'politica'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Política Ambiental
                </button>
              </div>

              {/* Contenido según tab activo */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {activeTab === 'uga' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <LayersIcon className="w-5 h-5 text-blue-500 mr-2" />
                          Clasificación de UGAs
                        </h3>
                        <img
                          src="images/clave_UGA_Sierra_Occidental.png"
                          alt="Clasificación de Unidades de Gestión Ambiental"
                          className="w-full h-auto rounded-lg shadow-md mb-4"
                        />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Mapa de clasificación de las Unidades de Gestión Ambiental (UGAs) 
                          de la región Sierra Occidental.
                        </p>
                      </div>
                      
                     {/* <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 
                        rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Leyenda de UGAs</h4>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                          <li className="flex items-center">
                            <div className="w-3 h-3 bg-green-500 rounded mr-2" />
                            UGA de Conservación
                          </li>
                          <li className="flex items-center">
                            <div className="w-3 h-3 bg-yellow-500 rounded mr-2" />
                            UGA de Aprovechamiento Sustentable
                          </li>
                          <li className="flex items-center">
                            <div className="w-3 h-3 bg-orange-500 rounded mr-2" />
                            UGA de Restauración
                          </li>
                          <li className="flex items-center">
                            <div className="w-3 h-3 bg-red-500 rounded mr-2" />
                            UGA de Protección Especial
                          </li>
                          <li className="flex items-center">
                            <div className="w-3 h-3 bg-blue-500 rounded mr-2" />
                            UGA Urbana
                          </li>
                        </ul>
                      </div>*/}
                    </>
                  )}

                  {activeTab === 'descripcion' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Info className="w-5 h-5 text-teal-500 mr-2" />
                          Descripción de las UGAs
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Las <strong>Unidades de Gestión Ambiental (UGAs)</strong> son la unidad mínima 
                          del territorio a la que se asignan determinados lineamientos y estrategias 
                          ecológicas para su manejo específico.
                        </p>
                        
                        <div className="space-y-4">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 
                            rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Características</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Unidad territorial homogénea</li>
                              <li>• Criterios específicos de manejo</li>
                              <li>• Estrategias ecológicas definidas</li>
                              <li>• Monitoreo individualizado</li>
                            </ul>
                          </div>
                          
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 
                            rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Objetivos</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Gestión ambiental específica</li>
                              <li>• Aplicación de lineamientos</li>
                              <li>• Evaluación de resultados</li>
                              <li>• Adaptación de estrategias</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'politica' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Target className="w-5 h-5 text-purple-500 mr-2" />
                          Política Ambiental
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          El presente <strong>Modelo de Ordenamiento Ecológico y Territorial Regional </strong> 
                           considera una zonificación, integrada por políticas que buscan definir el estado deseable del territorio mediante lineamientos, estrategias y criterios para conservar y controlar el deterioro del ambiente así como el aprovechamiento sustentable de los recursos naturales.
                        </p>
                        
                        {/* <div className="space-y-4">
                          <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Conservación</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Protección de ecosistemas y biodiversidad
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Aprovechamiento Sustentable</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Uso racional de recursos naturales
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-orange-500 pl-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Restauración</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Recuperación de áreas degradadas
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Protección</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Salvaguarda de áreas críticas
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-indigo-500 pl-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Desarrollo Urbano</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Crecimiento ordenado de asentamientos
                            </p>
                          </div>
                        </div> */}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Botones de acción */}
              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://siga.jalisco.gob.mx/ordenamiento/visores/visorsierraoccidental/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-teal-500 text-white 
                      rounded-lg text-sm font-medium text-center hover:shadow-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4 inline mr-1" />
                    Abrir en Nueva Ventana
                  </a>
                  {/* <button className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 
                    rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                    <Download className="w-4 h-4 inline mr-1" />
                    Descargar Capas
                  </button> */}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visor - Ocupa el resto del espacio */}
          <div className="flex-1">
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <GlobeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Visor GIS Sierra Occidental</h3>
                    <p className="text-sm text-gray-400">Sistema interactivo de información geográfica</p>
                  </div>
                </div>
                
                {/* Controles del visor */}
                {/* <div className="flex items-center gap-2">
                  <button className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors">
                    <ZoomInIcon className="w-5 h-5" />
                  </button>
                  <button className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors">
                    <ZoomOutIcon className="w-5 h-5" />
                  </button>
                  <button className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 
                    rounded-lg text-white transition-all">
                    <Maximize className="w-5 h-5" />
                  </button>
                </div> */}
              </div>
              
              <div className="h-[600px]">
                <iframe
                  src="https://siga.jalisco.gob.mx/ordenamiento/visores/visorsierraoccidental/index.html"
                  className="w-full h-full border-0"
                  title="Visor GIS Sierra Occidental"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Bitácora Ambiental Modernizada
const ModernBitacoraSection = ({ items }) => {
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Bitácora del Programa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Registro documental completo del proceso de ordenamiento ecológico
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={index}
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-6 rounded-2xl transition-all duration-300 text-left ${
                  expandedSection === index
                    ? `bg-gradient-to-br ${item.color} text-white shadow-2xl`
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl ${
                    expandedSection === index
                      ? 'bg-white/20'
                      : `bg-gradient-to-br ${item.color}`
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                </div>
                <p className={`text-sm ${
                  expandedSection === index ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {expandedSection === index ? 'Ver documentos ↓' : 'Ver documentos →'}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* Contenido expandido */}
        <AnimatePresence>
          {expandedSection !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                {items[expandedSection].content.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mr-3" />
                      {section.subtitle}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.items.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 
                            dark:hover:from-blue-900/20 dark:hover:to-teal-900/20 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 group-hover:text-teal-500 transition-colors" />
                            <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                              {doc.text}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {items[expandedSection].content.reduce((total, section) => total + section.items.length, 0)} documentos disponibles
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// Componente para la sección de descargas
const DescargasSection = ({ dataItems }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-cyan-500',
    emerald: 'from-emerald-500 to-green-500',
    amber: 'from-amber-500 to-orange-500',
    rose: 'from-rose-500 to-pink-500',
    indigo: 'from-indigo-500 to-purple-500',
    teal: 'from-teal-500 to-emerald-500'
  };

  return (
    <section id="descargas" className="py-16 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Recursos Especializados
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Herramientas y documentación específica para la gestión ambiental
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dataItems.map((item, index) => {
            const Icon = item.icon || FileCode;
            const colorClass = colorClasses[item.baseColor] || 'from-blue-500 to-teal-500';
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 
                  transition-opacity duration-300 rounded-2xl blur-xl"
                  style={{ 
                    background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                    backgroundImage: `linear-gradient(135deg, ${colorClass})`
                  }}
                />
                
                <div className="relative bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg 
                  border border-gray-100 dark:border-gray-700 overflow-hidden group-hover:shadow-xl transition-all">
                  
                  {/* Icono */}
                  <div className="mb-5">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorClass} 
                      flex items-center justify-center shadow-md`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>

                  {/* Contenido */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
                    {item.description}
                  </p>

                  {/* Botón de descarga */}
                  <motion.a
                    href={item.link}
                    target={item.target || "_blank"}
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
                      bg-gradient-to-r ${colorClass} text-white shadow hover:shadow-md transition-all`}
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </motion.a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Componente principal
const SierraOccidentalPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState(municipalities[0]);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const progress = (currentScroll / totalScroll) * 100;
      setScrollProgress(progress);
      setShowBackToTop(currentScroll > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };



    const handleDownload = (doc) => {
    console.log('Descargando:', doc.title);
    window.open(doc.url, '_blank');
  };

  // Configuración personalizada para Sierra
  const sierraConfig = {
    strings: {
      searchPlaceholder: 'Buscar en documentos de Sierra...',
      documents: 'Documentos disponibles'
    }
  };
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Back to top button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-40 p-3 bg-gradient-to-r from-blue-500 to-teal-500 
              text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 
              hover:scale-110 active:scale-95"
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
  {/* <EpicHero />
    Banner Moderno con fondo más claro */}
      <ModernBanner onScrollClick={scrollToSection} />
{/* <DocumentosEspecialesSection /> */}

 {/* Visor Completo con Sidebar */}
        <FullWidthVisorWithSidebar />


      <main>
        {/* Introducción */}
        <section id="introduccion" className="py-16 lg:py-24 bg-white dark:bg-gray-900 relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    Ordenamiento Ecológico Regional Sierra Occidental
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed text-justify">
                    El Gobierno del Estado de Jalisco impulsa el desarrollo sustentable en la región denominada Sierra Occidental, integrada por los municipios de Atenguillo, Guachinango, Mascota, Mixtlán, San Sebastián del Oeste y Talpa de Allende.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed text-justify">
                    Para impulsar dicho desarrollo, el día 17 de octubre de 2022 el Gobierno del Estado de Jalisco y los municipios firmaron un Convenio de Coordinación que garantiza la realización de las actividades programadas.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-justify">
                    Asimismo, se firmó con la Federación un Convenio Marco de Coordinación junto con SEMARNAT y SEDATU en marzo de 2020, lo que fortaleció la colaboración para la elaboración de los instrumentos de ordenamiento territorial.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  { label: 'Inicio de proceso de formulación', date: '22 noviembre 2022' },
                  { label: 'Inicio de Consulta Pública', date: '01 de febrero 2023' },
                  { label: 'Fin de Consulta Pública', date: '31 de mayo 2023' },
                  { label: 'Sesión de validación técnica', date: '5 de octubre 2023' },
                  { label: 'Fecha Publicación oficial', date: '30 noviembre 2024' },
                ].map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-6 border border-teal-200 dark:border-teal-800">
                    <div className="text-sm text-teal-600 dark:text-teal-400 font-semibold">{item.label}</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{item.date}</div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        <section id='DocumentosE' className="min-h-screen bg-[#020617] py-12 px-4 overflow-y-auto">
      {/* Página Sierra Occidental */}
      {/* <DocumentRepository 
        title="POETR" 
        subtitle="Sierra Occidental" 
        mainDocs={sierraMain} 
        annexDocs={sierraAnnex}
        accentColor="emerald"
      /> */}

      
 <DocumentRepository2
      items={sierraMain}
      // title="Región Sierra Occidental"
      // subtitle="Programa de Ordenamiento Territorial Ecológico Regional (POETR)"
      onDownload={(doc) => window.open(doc.url, '_blank')}
      showFooter={false}
    />

      
    </section>

        {/* Documentos Especiales (MOVIDO ARRIBA) */}
        

        {/* Recursos Especializados */}
         <Descargas dataItems={Items} />

<section className="municipalities-section py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 min-h-screen font-sans">
      <div className="container mx-auto max-w-7xl">
        
        {/* --- Título Principal --- */}
        <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 dark:text-teal-400 text-center mb-16 relative">
          <MapPin className="inline-block mr-3 mb-1 w-10 h-10" />
          Municipios de la Región : Sierra Occidental
          <p className="text-lg font-normal text-gray-600 dark:text-gray-400 mt-2">
            Conoce los municipios y sus características clave.
          </p>
        </h2>
        
        {/* --- Grid de Tarjetas (Selector) --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {municipalities.map((municipality) => (
            <div 
              key={municipality.id}
              onClick={() => setSelectedMunicipality(municipality)}
              className={`
                municipality-card 
                rounded-2xl 
                overflow-hidden 
                shadow-xl 
                transition-all 
                duration-500 
                cursor-pointer 
                hover:shadow-2xl 
                hover:scale-[1.03]
                bg-white/90 dark:bg-gray-800/80
                backdrop-blur-sm
                border border-gray-100 dark:border-gray-700
                ${selectedMunicipality.id === municipality.id 
                  ? 'ring-4 ring-teal-500 scale-[1.03] shadow-teal-300/50 dark:shadow-teal-700/50' 
                  : 'hover:ring-2 hover:ring-teal-300'
                }
              `}
            >
              <div className={`h-28 ${municipality.color} flex flex-col items-center justify-center p-3 text-white font-bold`}>
                <img 
                  src={municipality.shield} 
                  alt={`Escudo de ${municipality.name}`} 
                  className="h-14 w-14 object-contain mb-1 drop-shadow-md"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/000000/ffffff?text=E"; }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-extrabold text-center text-gray-900 dark:text-white">{municipality.name}</h3>
                <p className="text-xs text-center text-teal-600 dark:text-teal-400 mt-1 flex items-center justify-center">
                    Ver Detalles <ChevronRight className='w-3 h-3 ml-1'/>
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* --- Detalles del municipio seleccionado (Modern Panel) --- */}
        <div 
            key={selectedMunicipality.id} // Key para forzar la animación en cada selección
            className="municipality-details bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto border border-teal-200 dark:border-teal-900 animate-fade-in-up"
            style={{ '--delay': '200ms' }}
        >
          <div className="flex flex-col md:flex-row items-start gap-10">
            
            {/* Escudo y Descripción */}
            <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <img 
                src={selectedMunicipality.shield} 
                alt={`Escudo de ${selectedMunicipality.name}`} 
                className="h-48 w-48 object-contain shadow-lg rounded-full p-2 bg-white dark:bg-gray-900 mb-6 border-4 border-teal-300 dark:border-teal-500"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x192/000000/ffffff?text=E"; }}
              />
              <h3 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white text-center">{selectedMunicipality.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm text-center italic mt-2">{selectedMunicipality.description}</p>
            </div>
            
            {/* Información Detallada (Badges) */}
            <div className="w-full md:w-2/3">
                
                <h4 className="text-2xl font-bold text-teal-700 dark:text-teal-400 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center">
                    <Info className='w-5 h-5 mr-2'/> Datos Clave
                </h4>

                {/* Grid de Métricas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DetailBadge icon={Users} label="Población" value={selectedMunicipality.population} />
                    <DetailBadge icon={Map} label="Superficie" value={selectedMunicipality.area} />
                    <DetailBadge icon={Calendar} label="Municipalización" value={selectedMunicipality.foundation} />
                    <DetailBadge icon={Landmark} label="Región Administrativa" value={selectedMunicipality.administrativeRegion} />
                    <DetailBadge icon={MapPin} label="Número de UGAs" value={selectedMunicipality.NumerodeUgas} />
                    <DetailBadge icon={MountainSnow} label="Zonas de Importancia Ecológica" value={selectedMunicipality.EcologicaZona} />
                </div>
                
                {/* Atracciones (Full Width) */}
                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                        <TreePineIcon className='text-blue-500 mr-2 w-5 h-5'/> Principales Atracciones
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                        {selectedMunicipality.attractions}
                    </p>
                </div>
              
            </div>
          </div>
        </div>
      </div>
    </section>
        

        {/* Documentos Técnicos (MOVIDO ABAJO) */}
        

       
        {/* Bitácora Ambiental Modernizada */}
        <ModernBitacoraSection items={bitacoraItems} />

        {/* Footer */}
        {/* <footer className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <Leaf className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-lg font-semibold">Sierra Occidental</span>
                </div>
                <p className="text-gray-400 text-sm">
                  Programa de Ordenamiento Ecológico y Territorial
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4 text-sm">ACCESO DIRECTO</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><button onClick={() => scrollToSection('documentos-especiales')} className="hover:text-white transition-colors">Documentos Especiales</button></li>
                  <li><button onClick={() => scrollToSection('descargas')} className="hover:text-white transition-colors">Recursos Especializados</button></li>
                  <li><button onClick={() => scrollToSection('visor-completo')} className="hover:text-white transition-colors">Visor GIS Completo</button></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4 text-sm">RECURSOS</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><a href="https://transparencia.jalisco.gob.mx/" className="hover:text-white transition-colors">Transparencia</a></li>
                  <li><a href="https://datos.jalisco.gob.mx/" className="hover:text-white transition-colors">Datos Abiertos</a></li>
                  <li><a href="https://informes.jalisco.gob.mx/" className="hover:text-white transition-colors">Reportes</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4 text-sm">CONTACTO</h4>
                <p className="text-sm text-gray-400 mb-2">
                  Secretaría de Medio Ambiente y Desarrollo Territorial
                </p>
                <p className="text-sm text-gray-400">
                  contacto@ordenamiento.jalisco.gob.mx
                </p>
              </div>
            </div>
            
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-xs">
              <p>© {new Date().getFullYear()} Gobierno de Jalisco. Programa de Ordenamiento Ecológico Territorial.</p>
            </div>
          </div>
        </footer> */}
      </main>
    </div>
  );
};

export default SierraOccidentalPage;